/**
 * Main-thread pressure measurement.
 *
 * WHY
 * ---
 * The cleaned audio reaches the transmitted stream through
 *   render thread → MAIN THREAD → ONNX worker → MAIN THREAD → render thread
 * and the emit worklet buffers only EMIT_QUEUE_CAP(3) × 32 ms ≈ 96 ms. So a
 * main-thread stall longer than that punches a hole in what the far side hears
 * — twice over: silence while frozen, then dropped chunks when the backlog
 * floods the queue on release.
 *
 * That makes "how much is the main thread blocked?" the number we actually need
 * next to the dropout counters, which is what this module supplies.
 *
 * HOW
 * ---
 * Two independent measures, because neither alone is enough:
 *
 *   1. Long Tasks API — the browser reports every task ≥50 ms with its real
 *      duration. Exact, but blind to anything shorter, and not everywhere.
 *   2. Timer drift — a short self-rescheduling timer measures how late it runs.
 *      Catches sub-50 ms pressure and works without the API, but only samples.
 *
 * Both feed a rolling window so the panel can show a live rate rather than a
 * total that only grows.
 */
/** Length of the rolling window, in ms. Matches the alive-log cadence. */
const WINDOW_MS = 5000;
/** Timer period for the drift probe. Short enough to sample real stalls. */
const PROBE_MS = 50;
let _observer = null;
let _probeTimer = null;
let _probeLast = 0;
let _running = false;
/** Long tasks (≥50 ms) seen in the window. */
let _tasks = [];
/** Timer lateness samples in the window. */
let _drifts = [];
/** True when the browser reports long tasks; false → drift only. */
let _longTaskSupported = false;
/**
 * Drops samples that have fallen out of the rolling window.
 *
 * @param {number} now - Current timestamp.
 * @returns {void}
 */
function _prune(now) {
    const cutoff = now - WINDOW_MS;
    _tasks = _tasks.filter(t => t.at >= cutoff);
    _drifts = _drifts.filter(d => d.at >= cutoff);
}
/**
 * Starts measuring. Safe to call repeatedly.
 *
 * @returns {void}
 */
export function startMainThreadMonitor() {
    if (_running || typeof window === 'undefined') {
        return;
    }
    _running = true;
    _tasks = [];
    _drifts = [];
    // 1. Long Tasks — exact durations, straight from the browser.
    try {
        _observer = new PerformanceObserver(list => {
            for (const entry of list.getEntries()) {
                _tasks.push({ at: performance.now(), ms: entry.duration });
            }
        });
        _observer.observe({ entryTypes: ['longtask'] });
        _longTaskSupported = true;
    }
    catch {
        // Not supported (Safari, older browsers) — drift alone still gives a
        // usable pressure figure, so this is not fatal.
        _observer = null;
        _longTaskSupported = false;
    }
    // 2. Timer drift — how much later than asked did we actually run?
    _probeLast = performance.now();
    const probe = () => {
        if (!_running) {
            return;
        }
        const now = performance.now();
        const late = now - _probeLast - PROBE_MS;
        _probeLast = now;
        if (late > 0) {
            _drifts.push({ at: now, ms: late });
        }
        _prune(now);
        _probeTimer = setTimeout(probe, PROBE_MS);
    };
    _probeTimer = setTimeout(probe, PROBE_MS);
}
/**
 * Stops measuring and clears the window.
 *
 * @returns {void}
 */
export function stopMainThreadMonitor() {
    _running = false;
    if (_probeTimer) {
        clearTimeout(_probeTimer);
        _probeTimer = null;
    }
    if (_observer) {
        try {
            _observer.disconnect();
        }
        catch { /* ignore */ }
        _observer = null;
    }
    _tasks = [];
    _drifts = [];
}
/**
 * Returns the pressure figures for the rolling window.
 *
 * Pressure comes from long tasks when the browser reports them (real durations)
 * and falls back to summed timer lateness otherwise. Lateness understates a
 * stall — the probe only wakes every {@link PROBE_MS} — so the fallback is a
 * floor, not an equal substitute; {@link IMainThreadStats.exact} says which.
 *
 * @returns {IMainThreadStats}
 */
export function getMainThreadStats() {
    const now = performance.now();
    _prune(now);
    const blockedMs = _tasks.reduce((a, t) => a + t.ms, 0);
    const worstMs = _tasks.reduce((a, t) => (t.ms > a ? t.ms : a), 0);
    const driftSum = _drifts.reduce((a, d) => a + d.ms, 0);
    const driftMaxMs = _drifts.reduce((a, d) => (d.ms > a ? d.ms : a), 0);
    const effectiveBlocked = _longTaskSupported ? blockedMs : driftSum;
    return {
        pressurePct: Math.min(100, effectiveBlocked / WINDOW_MS * 100),
        blockedMs,
        longTasks: _tasks.length,
        worstMs,
        driftAvgMs: _drifts.length ? driftSum / _drifts.length : 0,
        driftMaxMs,
        exact: _longTaskSupported
    };
}
/**
 * Whether the monitor is currently running.
 *
 * @returns {boolean}
 */
export function isMainThreadMonitorRunning() {
    return _running;
}
